import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { parseCVText } from './cvParser';

// Use fonts from the public/ folder.
// CRA serves public/ files at the PUBLIC_URL root. Using absolute URLs ensures
// @react-pdf/font recognises them via is-url and fetches them as ArrayBuffers.
const FONT_BASE =
  (typeof window !== "undefined" ? window.location.origin : "") +
  (process.env.PUBLIC_URL || "") +
  "/fonts";

const fontConfig = [
  {
    family: "Tahoma",
    fonts: [
      { src: `${FONT_BASE}/Tahoma.woff` },
      { src: `${FONT_BASE}/Tahoma.woff`, fontWeight: "bold" },
    ],
  },
  {
    family: "PTSans",
    fonts: [
      { src: `${FONT_BASE}/PTSans.woff` },
      { src: `${FONT_BASE}/PTSansBold.woff`, fontWeight: "bold" },
    ],
  },
  {
    family: "TimesNewRoman",
    fonts: [
      { src: `${FONT_BASE}/TimesNewRomanMTStd.woff` },
      { src: `${FONT_BASE}/TimesNewRomanMTStd-Bold.woff`, fontWeight: "bold" },
    ],
  },
];

fontConfig.forEach(config => {
  try {
    Font.register(config);
  } catch (e) {
    console.error(`Error registering font ${config.family}:`, e);
  }
});

const CVDocument = ({ content, config, sections: sectionsProp }) => {
  const {
    fontFamily = 'Tahoma',
    fontSize = 10,
    lineHeight = 1.4,
    primaryColor = '#191970', // Dark Blue
    secondaryColor = '#000000', // Black body text
    headingColor = '#191970',
    boldHeadings = true,
  } = config || {};

  const styles = StyleSheet.create({
    page: {
      padding: 40,
      backgroundColor: "#FFFFFF",
      fontFamily: fontFamily,
    },
    section: {
      marginBottom: 15,
    },
    text: {
      fontSize: fontSize,
      color: secondaryColor,
      lineHeight: lineHeight,
      fontFamily: fontFamily,
    },
    sectionTitle: {
      fontSize: fontSize + 3,
      color: headingColor,
      fontWeight: boldHeadings ? "bold" : "normal",
      textTransform: "uppercase",
      marginTop: 14,
      marginBottom: 7,
      borderBottom: `1pt solid ${headingColor}`,
      paddingBottom: 2,
      fontFamily: fontFamily,
    },
    experienceTitle: {
      fontSize: fontSize + 1,
      color: primaryColor,
      fontWeight: "bold",
      marginTop: 6,
      marginBottom: 2,
      fontFamily: fontFamily,
    },
    headerContainer: {
      marginBottom: 20,
      paddingBottom: 10,
    },
    headerName: {
      fontSize: 22,
      color: secondaryColor,
      fontWeight: "bold",
      marginBottom: 3,
      fontFamily: fontFamily,
      textAlign: "center",
    },
    headerTitle: {
      fontSize: 13,
      color: secondaryColor,
      marginBottom: 3,
      fontFamily: fontFamily,
      textAlign: "center",
    },
    headerContact: {
      fontSize: fontSize,
      color: primaryColor,
      marginBottom: 2,
      fontFamily: fontFamily,
      textAlign: "center",
    },
    date: {
      fontSize: fontSize - 1,
      color: secondaryColor,
      fontStyle: "italic",
      marginBottom: 4,
      fontFamily: fontFamily,
    },
    bulletPoint: {
      flexDirection: "row",
      marginBottom: 2,
      paddingLeft: 10,
    },
    bullet: {
      width: 10,
      fontSize: fontSize,
      fontFamily: fontFamily,
    },
    bulletText: {
      flex: 1,
      fontSize: fontSize,
      color: secondaryColor,
      fontFamily: fontFamily,
    },
    checkLine: {
      flexDirection: "row",
      alignItems: "flex-start",
      marginTop: 3,
      marginBottom: 3,
      paddingLeft: 6,
    },
    checkMark: {
      fontSize: fontSize,
      color: primaryColor,
      fontWeight: "bold",
      fontFamily: fontFamily,
      marginRight: 6,
      flexShrink: 0,
    },
    checkText: {
      flex: 1,
      fontSize: fontSize,
      color: secondaryColor,
      fontFamily: fontFamily,
    },
  });

  const sections = sectionsProp || parseCVText(content);

  const renderLine = (line, idx) => {
    switch (line.type) {
      case "empty":
        return <View key={idx} style={{ height: 10 }} />;
      case "headerName":
        return <Text key={idx} style={styles.headerName}>{line.text}</Text>;
      case "headerTitle":
        return <Text key={idx} style={styles.headerTitle}>{line.text}</Text>;
      case "headerContact":
        return <Text key={idx} style={styles.headerContact}>{line.text}</Text>;
      case "role":
        return (
          <Text key={idx} style={[styles.experienceTitle, { marginBottom: 2, marginTop: 0 }]}>
            {line.text}
          </Text>
        );
      case "experienceHeader":
        return (
          <View key={idx} style={{ flexDirection: "row", alignItems: "flex-start", marginTop: 6, marginBottom: 2 }}>
            <Text style={[styles.experienceTitle, { flexShrink: 0, marginTop: 0 }]}>
              {line.titlePart}
            </Text>
            {line.infoPart ? (
              <Text style={[styles.text, { marginLeft: 4, flex: 1, marginTop: 1 }]}>
                | {line.infoPart}
              </Text>
            ) : null}
          </View>
        );
      case "boldCategory":
        return (
          <Text key={idx} style={[styles.text, { marginTop: 4 }]}>
            <Text style={{ fontWeight: "bold", fontFamily: fontFamily }}>{line.boldPart}</Text>
            <Text>{" " + line.regularPart}</Text>
          </Text>
        );
      case "boldLine":
        return (
          <Text key={idx} style={[styles.experienceTitle, { color: secondaryColor }]}>
            {line.text}
          </Text>
        );
      case "check":
        return (
          <View key={idx} style={styles.checkLine}>
            <Text style={styles.checkMark}>•</Text>
            <Text style={styles.checkText}>{line.text}</Text>
          </View>
        );
      case "bullet":
        return (
          <View key={idx} style={styles.bulletPoint}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>{line.text}</Text>
          </View>
        );
      default:
        return <Text key={idx} style={styles.text}>{line.text}</Text>;
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          {sections
            .filter((s) => !s.hidden)
            .map((section) => {
              const align = section.align || "left";
              if (section.type === "header") {
                return (
                  <View key={section.id} style={[styles.headerContainer, { textAlign: align }]}>
                    {section.lines.map(renderLine)}
                  </View>
                );
              }
              return (
                <View key={section.id} style={{ textAlign: align }}>
                  {section.title ? (
                    <Text style={styles.sectionTitle}>{section.title}</Text>
                  ) : null}
                  {section.lines.map(renderLine)}
                </View>
              );
            })}
        </View>
      </Page>
    </Document>
  );
};

export default CVDocument;
